import axios, { AxiosInstance, AxiosResponse, AxiosError } from 'axios';
import { getAuthHeaders } from '../store/authStore';

// API Configuration
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:4000/api';

class ApiService {
  private instance: AxiosInstance;

  constructor() {
    this.instance = axios.create({
      baseURL: API_BASE_URL,
      timeout: 10000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.setupInterceptors();
  }

  private setupInterceptors() {
    // Request interceptor - Add auth headers
    this.instance.interceptors.request.use(
      (config) => {
        const authHeaders = getAuthHeaders();
        if (authHeaders.Authorization) {
          config.headers.Authorization = authHeaders.Authorization;
        }

         console.log("➡️ Request URL:", `${config.baseURL ?? ''}${config.url ?? ''}`);
        console.log("➡️ Headers:", config.headers);
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor - Handle errors
    this.instance.interceptors.response.use(
      (response: AxiosResponse) => response,
      async (error: AxiosError) => {
        const originalRequest = error.config as any;
        const status = error.response?.status;

        if (status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;

          try {
            const refreshToken = localStorage.getItem('refreshToken');
            if (refreshToken) {
              // Create a new axios instance to avoid circular calls
              const refreshInstance = axios.create({
                baseURL: API_BASE_URL,
                headers: { 'Content-Type': 'application/json' }
              });

              const refreshResponse = await refreshInstance.post('/auth/refresh', {
                refreshToken,
              });

              const { accessToken, refreshToken: newRefreshToken } = refreshResponse.data.data;

              localStorage.setItem('accessToken', accessToken);
              localStorage.setItem('refreshToken', newRefreshToken);

              originalRequest.headers.Authorization = `Bearer ${accessToken}`;
              return this.instance(originalRequest);
            }
          } catch (refreshError) {
            localStorage.removeItem('accessToken');
            localStorage.removeItem('refreshToken');
            window.location.href = '/login';
            return Promise.reject(refreshError);
          }
        }

        if (status === 403) {
          console.error('Access denied: Insufficient permissions');
        } else if (status === 429) {
          console.error('Rate limit exceeded. Please try again later.');
        } else if (status && status >= 500) {
          console.error('Server error occurred. Please try again later.');
        }

        return Promise.reject(error);
      }
    );
  }

  // HTTP Methods
  get<T = any>(url: string, config = {}) {

    return this.instance.get<T>(url, config);
  }

  post<T = any>(url: string, data = {}, config = {}) {
    return this.instance.post<T>(url, data, config);
  }

  put<T = any>(url: string, data = {}, config = {}) {
    return this.instance.put<T>(url, data, config);
  }

  patch<T = any>(url: string, data = {}, config = {}) {
    return this.instance.patch<T>(url, data, config);
  }

  delete<T = any>(url: string, config = {}) {
    return this.instance.delete<T>(url, config);
  }

  // Upload method for file uploads
  upload<T = any>(url: string, formData: FormData, onProgress?: (progress: number) => void) {
    return this.instance.post<T>(url, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: (progressEvent) => {
        if (onProgress && progressEvent.total) {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onProgress(progress);
        }
      },
    });
  }
}

export const api = new ApiService();